import { useCallback, useEffect, useRef, useState } from "react";
import type { CctvCamera } from "../map/layers";
import {
  createEngine,
  hazeSeries,
  motionSeries,
  summarise,
  type CvEngineState,
} from "../cv/engine";
import { hazeSeverity } from "../cv/haze";
import { trafficBand } from "../cv/traffic";

/**
 * Runs the detectors over the live wall and republishes a cheap-to-render view.
 *
 * Two constraints shape this hook.
 *
 * CANVAS TAINTING. Reading pixels back out of a canvas is only possible if the
 * image was fetched CORS-clean. Pointed straight at a vendor host it would
 * throw on the first frame, so the engine must be fed through our own
 * `/api/cctv/snapshot/:id` relay with `crossOrigin="anonymous"` — which is
 * already the URL the map layer uses. Same bytes, one request path, and the
 * vendor URL never reaches the browser.
 *
 * COST. A tile is 320×240 and the engine downsamples to 160 wide, so one camera
 * is a few hundred microseconds. The wall deliberately analyses ONE camera per
 * tick, round-robin, at ~1.2 Hz. Analysing every tile every frame would put
 * six decodes in front of a 60 fps map for a signal that does not change fast.
 */

export interface CvReading {
  cameraId: string;
  aod: number | null;
  veiledFraction: number | null;
  hazeLabel: string;
  /** Set when the camera is dead or the frame is unjudgeable. */
  frameIssue?: "no-signal" | "no-structure";
  hazeSeverity: string;
  motion: number | null;
  blobs: number;
  motionLabel: string;
  hazeSeries: number[];
  motionSeries: number[];
  samples: number;
  sampledAt: number | null;
  transportError?: boolean;
}

export interface CvWallView {
  byCamera: Record<string, CvReading>;
  analysed: number;
  tracked: number;
  haziest: { id: string; aod: number } | null;
  busiest: { id: string; motion: number; blobs: number } | null;
  running: boolean;
}

const TICK_MS = 850;
const MAX_TRACKED = 12;

function toView(state: CvEngineState, running: boolean): CvWallView {
  const byCamera: Record<string, CvReading> = {};
  for (const [id, track] of state.tracks) {
    const h = track.lastHaze;
    const t = track.lastTraffic;
    const severity = h ? hazeSeverity(h.aod) : null;
    const tb = t ? trafficBand(t.motion, t.blobs.length) : null;
    byCamera[id] = {
      cameraId: id,
      // null means "not measured yet" or "declined to answer" — never zero.
      // A haze reading of 0 and a camera we have not looked at must not look
      // the same on screen, or the panel quietly under-reports.
      // The sourced quantity. There is deliberately no 0-100 "haze index" here:
      // AOD is what the dark-channel prior estimates, and it is what we show.
      aod: h?.aod ?? null,
      veiledFraction: h?.veiledFraction ?? null,
      // A dead camera gets its own label, never the haze band. "clear" on a
      // blank feed is the all-clear nobody asked for.
      hazeLabel: !track.lastHealth?.usable
        ? track.lastHealth?.reason === "no-signal"
          ? "NO SIGNAL"
          : "no reading"
        : (severity ?? "no reading"),
      frameIssue: track.lastHealth?.usable ? undefined : track.lastHealth?.reason,
      hazeSeverity: severity ?? "none",
      motion: t?.motion ?? null,
      blobs: t?.blobs.length ?? 0,
      motionLabel: tb?.label ?? "no reading",
      hazeSeries: hazeSeries(track),
      motionSeries: motionSeries(track),
      samples: track.samples.length,
      sampledAt: track.samples.at(-1)?.at ?? null,
    };
  }
  const s = summarise(state);
  return {
    byCamera,
    analysed: state.totalAnalysed,
    tracked: s.tracked,
    haziest: s.haziest,
    busiest: s.busiest,
    running,
  };
}

export function useCvWall(cameras: CctvCamera[], apiBase: string, enabled = true): CvWallView {
  const engineRef = useRef<CvEngineState>(createEngine());
  const [view, setView] = useState<CvWallView>(() => toView(engineRef.current, false));
  const idsRef = useRef<string[]>([]);
  const failuresRef = useRef(new Set<string>());

  // Cameras arrive as a new array identity on every feed refresh; keep a stable
  // id list so the round-robin cursor is not reset by a refetch.
  const ids = cameras.slice(0, MAX_TRACKED).map((c) => c.id);
  const key = ids.join("|");
  if (idsRef.current.join("|") !== key) idsRef.current = ids;

  const snapshotUrl = useCallback(
    (id: string) => `${apiBase}/api/cctv/snapshot/${encodeURIComponent(id)}`,
    [apiBase],
  );

  useEffect(() => {
    let alive = true;
    let timer: number | undefined;
    let cursor = 0;
    let cancelLoad: (() => void) | undefined;
    const failures = failuresRef.current;
    const publishView = (running: boolean) => {
      const next = toView(engineRef.current, running);
      for (const id of failures) {
        next.byCamera[id] = { cameraId: id, hazeLabel: "no reading", hazeSeverity: "none", blobs: 0, motionLabel: "no reading", hazeSeries: [], motionSeries: [], samples: 0, sampledAt: null, transportError: true, aod: null, veiledFraction: null, motion: null };
      }
      // A transport failure must not win the wall's headline summaries.
      if (failures.has(next.haziest?.id ?? '')) next.haziest = null;
      if (failures.has(next.busiest?.id ?? '')) next.busiest = null;
      if (alive) setView(next);
    };
    const tick = async () => {
      if (!alive) return;
      const running = enabled && !document.hidden;
      if (running && idsRef.current.length) {
        const id = idsRef.current[cursor++ % idsRef.current.length];
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.decoding = "async";
        const loaded = await new Promise<boolean>((resolve) => {
          const finish = (ok: boolean) => {
            window.clearTimeout(deadline);
            img.onload = null; img.onerror = null;
            resolve(ok);
          };
          const deadline = window.setTimeout(() => finish(false), 5000);
          cancelLoad = () => finish(false);
          img.onload = () => finish(true);
          img.onerror = () => finish(false);
          img.src = `${snapshotUrl(id)}?_ts=${Date.now()}`;
        });
        if (!alive) return;
        if (loaded && img.naturalWidth > 0) {
          const c = document.createElement("canvas");
          // Bound decoded analysis work even when a vendor sends a large still.
          const scale = Math.min(160 / img.naturalWidth, 160 / img.naturalHeight);
          c.width = Math.max(1, Math.round(img.naturalWidth * scale)); c.height = Math.max(1, Math.round(img.naturalHeight * scale));
          const ctx = c.getContext("2d", { willReadFrequently: true });
          try {
            if (!ctx) throw new Error("Canvas unavailable");
            ctx.drawImage(img, 0, 0, c.width, c.height);
            const { analyseFrame } = await import("../cv/engine");
            if (!alive) return;
            analyseFrame(engineRef.current, id, ctx.getImageData(0, 0, c.width, c.height).data, c.width, c.height, Date.now());
            failures.delete(id);
          } catch { failures.add(id); }
        } else failures.add(id);
      }
      for (const id of failures) {
        const track = engineRef.current.tracks.get(id);
        if (track) track.previous = null;
      }
      publishView(running);
      if (alive) timer = window.setTimeout(tick, TICK_MS);
    };
    publishView(false);
    if (enabled) void tick();
    return () => {
      alive = false;
      if (timer) window.clearTimeout(timer);
      cancelLoad?.();
    };
  }, [enabled, key, snapshotUrl]);

  return view;
}
